// Assigning material that has already been received or billed to where it was actually used —
// a Work Item / activity, and a building or unit type — so actual cost can, over time, be compared
// against the estimate. This never affects ordering, receiving or billing; it is a separate, later step,
// and nothing here is required before those can happen.
import type { MaterialProcurementOrder, MaterialRateItem, ProjectEstimateScopeItem, QuantityProfileComponent, UnitTypeMaster } from '../pages/OfficeEstimationPage';
import { groupLabel } from './materialReceiving';
import { matchesRow, type StockIssue } from './materialStock';

const round3 = (value: number) => Math.round((value + Number.EPSILON) * 1000) / 1000;
const roundMoney = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;
const num = (value: unknown) => { const n = Number(value); return Number.isFinite(n) ? n : 0; };

// ── What a delivery or bill line can be allocated to ─────────────────────────
export type AllocationDestination = {
  /** A component inside a unit type's own quantity profile ("RCC Slab" inside "2BHK"), a line from
   * the project/building/common estimate, or the catch-all "General / Unspecified" when the exact
   * activity genuinely isn't known. */
  kind: 'unitTypeComponent' | 'scopeItem' | 'general';
  id: string;
  label: string;
  group: string;
  subgroup?: string;
  unitTypeId?: string;
  unitTypeLabel?: string;
  /** Only set for a scope-item destination, which already carries its own scope. */
  scopeType?: 'common' | 'building' | 'project' | 'unitType';
  buildingName?: string;
  sourceType?: string;
  sourceId?: string;
  quoteOptionId?: string;
  assemblyType?: string;
  variety?: string;
};

/**
 * For when a delivery's exact activity genuinely can't be pinned down. Recording against this keeps
 * the material off the unallocated list and its quantity out of any one Work Item's actual cost —
 * instead of guessing a split, or (worse) tagging several activities with the same un-split quantity,
 * which would double-count it. Always offered, first, alongside the estimate's own activities.
 */
export const GENERAL_DESTINATION: AllocationDestination = { kind: 'general', id: 'general', label: 'General / Unspecified', group: 'General' };

/**
 * Every activity already defined in this project's estimate, searchable as one list: each unit type's
 * own components, and every project/building/common scope line. Deduplicated so the same activity
 * inside the same unit type, or the same scope line, only appears once.
 */
/**
 * A scope line or unit-type component's name exactly as the estimate itself shows it: variety and
 * assembly type joined ("24 x 48 · Balcony Tile"), deduplicated when they say the same thing ("Cement"
 * not "Cement · Cement"), falling back to its own label when it has neither. Matches Project Estimate's
 * own `lineDisplayTitle`, so anything visible there is findable — and distinguishable — here: two
 * components can share a variety ("24 x 48") while sitting under different assemblies ("Balcony Tile"
 * vs. "Flooring Tiles"), and only the assembly type tells them apart.
 */
function varietyDisplayLabel(item: Pick<ProjectEstimateScopeItem | QuantityProfileComponent, 'label' | 'variety' | 'assemblyType'>): string {
  const values = [item.variety, item.assemblyType].map((value) => (value || '').trim()).filter(Boolean);
  const unique = values.filter((value, index) => values.findIndex((other) => other.toLowerCase() === value.toLowerCase()) === index);
  return unique.join(' · ') || (item.label || '').trim();
}

/**
 * Whether a line is pure labour/workmanship rather than something a material delivery could actually
 * go toward — a bag of cement was never "used" by "Tile Fixing Labour". A scope item says this about
 * itself directly; a unit-type component only says which material or assembly it's linked to, so we
 * look that material up and check its own group.
 */
function isLabourLine(item: { sourceType?: string; sourceId?: string }, materialItems: Pick<MaterialRateItem, 'id' | 'group'>[]): boolean {
  if (item.sourceType === 'labour') return true;
  if (!item.sourceId) return false;
  const material = materialItems.find((candidate) => candidate.id === item.sourceId);
  return material?.group === 'labour';
}

export function destinationOptions(
  unitTypes: Pick<UnitTypeMaster, 'id' | 'label' | 'components'>[],
  scopeItems: Pick<ProjectEstimateScopeItem, 'id' | 'label' | 'variety' | 'assemblyType' | 'group' | 'subgroup' | 'scopeType' | 'buildingName' | 'sourceType' | 'sourceId' | 'quoteOptionId'>[],
  materialItems: Pick<MaterialRateItem, 'id' | 'group'>[] = [],
): AllocationDestination[] {
  const seen = new Set<string>();
  const options: AllocationDestination[] = [];
  const add = (option: AllocationDestination) => {
    const key = `${option.kind}:${option.id}`;
    if (seen.has(key)) return;
    seen.add(key);
    options.push(option);
  };
  for (const unit of unitTypes) {
    for (const component of (unit.components || []) as QuantityProfileComponent[]) {
      if (isLabourLine(component, materialItems)) continue;
      const label = varietyDisplayLabel(component);
      if (!label) continue;
      add({
        kind: 'unitTypeComponent', id: component.id, label, group: (component.group || 'Other').trim(), unitTypeId: unit.id, unitTypeLabel: unit.label,
        sourceType: component.sourceType, sourceId: component.sourceId, quoteOptionId: component.quoteOptionId, assemblyType: component.assemblyType, variety: component.variety,
      });
    }
  }
  for (const item of scopeItems) {
    if (isLabourLine(item, materialItems)) continue;
    const label = varietyDisplayLabel(item);
    if (!label) continue;
    add({
      kind: 'scopeItem', id: item.id, label, group: (item.group || 'Other').trim(), subgroup: item.subgroup?.trim() || undefined,
      scopeType: item.scopeType, buildingName: item.scopeType === 'building' ? item.buildingName : undefined,
      sourceType: item.sourceType, sourceId: item.sourceId, quoteOptionId: item.quoteOptionId, assemblyType: item.assemblyType, variety: item.variety,
    });
  }
  return [GENERAL_DESTINATION, ...options.sort((a, b) => a.group.localeCompare(b.group) || a.label.localeCompare(b.label))];
}

/** Destinations whose group, subgroup, label, building, or unit type contains every word typed. */
export function searchDestinations(options: AllocationDestination[], query: string): AllocationDestination[] {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return options;
  return options.filter((option) => {
    const haystack = `${option.group} ${option.subgroup || ''} ${option.label} ${option.unitTypeLabel || ''} ${option.buildingName || ''}`.toLowerCase();
    return words.every((word) => haystack.includes(word));
  });
}

// ── Material already received or billed, and how much of it is still unallocated ────────────────────
export type AllocationSource = {
  key: string;
  sourceType: 'receipt' | 'bill';
  orderId?: string; receiptId?: string;
  billId?: string; billLineKey?: string;
  materialId?: string; quoteOptionId?: string; materialLabel: string; unit: string;
  /** Set only for a receipt of a bundled, multi-assembly-type material — matches a TrackedMaterial row's
   * `assemblyType` in materialStock.ts, so its Stock-usage reference can be looked up. */
  materialAssemblyType?: string;
  /** Total quantity this source represents, and its per-unit rate (0 if never priced). */
  qty: number; rate: number;
  vendor: string; date: string; reference: string;
};
export type SavedAllocation = { id: string; sourceKey: string; qty: number; stockIssueId?: string };

const withAllocated = (source: AllocationSource, allocations: SavedAllocation[]) => {
  const allocated = round3(allocations.filter((entry) => entry.sourceKey === source.key).reduce((sum, entry) => sum + num(entry.qty), 0));
  return { ...source, allocated, remaining: round3(Math.max(0, source.qty - allocated)) };
};
export type AllocationSourceWithBalance = AllocationSource & { allocated: number; remaining: number };

function inferredAssemblyTypeFromLabel(materialLabel?: string): string {
  const parts = String(materialLabel || '').split('·').map((part) => part.trim()).filter(Boolean);
  if (parts.length < 3) return '';
  const parent = parts[0].toLowerCase();
  // Older no-PO receipts for bundled stock materials sometimes missed materialAssemblyType even
  // though the visible label carried it as "Parent · Assembly · Variety".
  if (!/(material|concrete|rmc|aggregate|sand)/i.test(parent)) return '';
  return parts[1] || '';
}

/** Every receipt across every order, as an allocation source (one per delivery). */
export function sourcesFromOrders(orders: Pick<MaterialProcurementOrder, 'id' | 'materialId' | 'quoteOptionId' | 'materialAssemblyType' | 'materialLabel' | 'unit' | 'vendor' | 'rateSnapshot' | 'receipts' | 'unplannedRejected'>[]): AllocationSource[] {
  const sources: AllocationSource[] = [];
  for (const order of orders) {
    if (order.unplannedRejected) continue;
    for (const receipt of order.receipts || []) {
      const qty = num(receipt.qty);
      if (qty <= 0) continue;
      sources.push({
        key: `receipt:${order.id}:${receipt.id}`, sourceType: 'receipt', orderId: order.id, receiptId: receipt.id,
        materialId: order.materialId, quoteOptionId: order.quoteOptionId, materialAssemblyType: order.materialAssemblyType || inferredAssemblyTypeFromLabel(order.materialLabel), materialLabel: order.materialLabel, unit: order.unit,
        qty, rate: num(order.rateSnapshot), vendor: String(order.vendor || '').trim() || 'Vendor not set', date: receipt.date,
        reference: receipt.reference || receipt.billNumber || '',
      });
    }
  }
  return sources;
}

/** Every line of a bill that has no order or delivery behind it — the only bills with material to allocate. */
export function sourcesFromDirectBills(bills: Array<{ id: string; vendor: string; invoiceDate: string; invoiceNumber: string; sourceType?: string; lines: Array<{ materialId?: string; quoteOptionId?: string; materialLabel: string; unit: string; qty: number; rate: number }> }>): AllocationSource[] {
  const sources: AllocationSource[] = [];
  for (const bill of bills) {
    if (bill.sourceType !== 'direct') continue;
    bill.lines.forEach((line, index) => {
      if (num(line.qty) <= 0 || !line.unit) return; // a charge line (freight, testing) has no unit and nothing physical to allocate
      const billLineKey = `${line.materialId || line.materialLabel}_${index}`;
      sources.push({
        key: `bill:${bill.id}:${billLineKey}`, sourceType: 'bill', billId: bill.id, billLineKey,
        materialId: line.materialId, quoteOptionId: line.quoteOptionId, materialLabel: line.materialLabel, unit: line.unit,
        qty: num(line.qty), rate: num(line.rate), vendor: String(bill.vendor || '').trim() || 'Vendor not set', date: bill.invoiceDate, reference: bill.invoiceNumber,
      });
    });
  }
  return sources;
}

/** Sources with any quantity still unallocated, most recent first. */
export function unallocatedSources(sources: AllocationSource[], allocations: SavedAllocation[]): AllocationSourceWithBalance[] {
  return sources.map((source) => withAllocated(source, allocations))
    .filter((source) => source.remaining > 0.001)
    .sort((a, b) => b.date.localeCompare(a.date));
}

/** The material's own category ("Cement", "Concrete Materials") — the same grouping Receiving uses. */
export function sourceGroup(source: Pick<AllocationSource, 'materialId' | 'materialLabel'>, materialItems: Pick<MaterialRateItem, 'id' | 'group'>[]): string {
  const item = source.materialId ? materialItems.find((candidate) => candidate.id === source.materialId) : undefined;
  if (item) return groupLabel(String(item.group));
  const first = source.materialLabel.split('·')[0]?.trim();
  return first || 'Other';
}

/** Unallocated sources grouped by material category, each group sorted newest first. */
export function groupSourcesByMaterial(sources: AllocationSourceWithBalance[], materialItems: Pick<MaterialRateItem, 'id' | 'group'>[]): Array<{ group: string; sources: AllocationSourceWithBalance[] }> {
  const groups = new Map<string, AllocationSourceWithBalance[]>();
  for (const source of sources) {
    const group = sourceGroup(source, materialItems);
    groups.set(group, [...(groups.get(group) || []), source]);
  }
  return Array.from(groups.entries())
    .map(([group, list]) => ({ group, sources: list }))
    .sort((a, b) => a.group.localeCompare(b.group));
}

// ── Stock usage, shown as reference only (never auto-linked) while allocating a Stock-tracked material ──
/** When a stock issue was recorded, in milliseconds — `recordedAt` is a Firestore server timestamp once
 * read back (not the `string` the type says), so this accepts whatever shape actually comes over the
 * wire and falls back to the plain usage `date` if there's no timestamp at all. */
function issueTimeMs(issue: Pick<StockIssue, 'recordedAt' | 'date'>): number {
  const recorded: any = issue.recordedAt;
  if (recorded && typeof recorded.toMillis === 'function') return recorded.toMillis();
  if (recorded && typeof recorded.seconds === 'number') return recorded.seconds * 1000;
  if (typeof recorded === 'string' && recorded) { const parsed = Date.parse(recorded); if (!Number.isNaN(parsed)) return parsed; }
  const parsed = Date.parse(`${issue.date}T00:00:00`);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** Every stock issue recorded against one material (or one row of a bundled item), newest first — shown
 * next to a source being allocated so the qty/destination split can be picked with that in mind. Never
 * auto-fills or links to the allocation itself; it's reference only. */
export function stockUsageForMaterial(issues: StockIssue[], rootId: string, assemblyType?: string): StockIssue[] {
  return issues
    .filter((issue) => issue.materialId === rootId && matchesRow(issue.materialAssemblyType, assemblyType))
    .sort((a, b) => issueTimeMs(b) - issueTimeMs(a));
}

/** How many of a material's stock issues were recorded after this admin last opened its usage list —
 * a missing `lastSeenIso` (never opened) counts every existing issue as new. */
export function newStockUsageCount(issues: StockIssue[], rootId: string, assemblyType: string | undefined, lastSeenIso?: string): number {
  const lastSeenMs = lastSeenIso ? Date.parse(lastSeenIso) : 0;
  return stockUsageForMaterial(issues, rootId, assemblyType).filter((issue) => issueTimeMs(issue) > lastSeenMs).length;
}

export type PendingStockUsage = StockIssue & { allocated: number; remaining: number };

/** Stock usage that has not yet been linked to saved material allocations. Fully allocated usage drops
 * out; partially allocated usage remains with only the balance. */
export function pendingStockUsageForMaterial(
  issues: StockIssue[],
  allocations: Array<Pick<SavedAllocation, 'stockIssueId' | 'qty'>>,
  rootId: string,
  assemblyType?: string,
): PendingStockUsage[] {
  return stockUsageForMaterial(issues, rootId, assemblyType)
    .map((issue) => {
      const allocated = round3(allocations.filter((allocation) => allocation.stockIssueId === issue.id).reduce((sum, allocation) => sum + num(allocation.qty), 0));
      return { ...issue, allocated, remaining: round3(Math.max(0, num(issue.qty) - allocated)) };
    })
    .filter((issue) => issue.remaining > 0.001);
}

// ── Saving a split ───────────────────────────────────────────────────────────
export type AllocationDraftLine = { destination: AllocationDestination | null; destinations?: AllocationDestination[]; qty: string; note: string; stockIssueId?: string };

const lineDestinations = (line: Pick<AllocationDraftLine, 'destination' | 'destinations'>): AllocationDestination[] => {
  const destinations = Array.isArray(line.destinations) ? line.destinations.filter(Boolean) : [];
  if (destinations.length) return destinations;
  return line.destination ? [line.destination] : [];
};

/** What's wrong with a draft split, if anything; empty when it's ready to save. */
export function validateAllocationDraft(source: AllocationSourceWithBalance, lines: AllocationDraftLine[]): string[] {
  const problems: string[] = [];
  const filled = lines.filter((line) => lineDestinations(line).length && num(line.qty) > 0);
  if (!filled.length) problems.push('add at least one work item with a quantity');
  const total = round3(filled.reduce((sum, line) => sum + num(line.qty), 0));
  if (total - source.remaining > 0.005) problems.push(`only ${source.remaining} ${source.unit} is left to allocate`);
  return problems;
}

/** The allocation records to write for a source's draft split. */
export function planAllocationSave(source: AllocationSourceWithBalance, lines: AllocationDraftLine[], allocatedDate: string) {
  return lines
    .map((line) => ({ line, destinations: lineDestinations(line) }))
    .filter(({ destinations, line }) => destinations.length && num(line.qty) > 0)
    .map(({ line, destinations }) => {
      const qty = num(line.qty);
      return {
        sourceKey: source.key,
        sourceType: source.sourceType,
        orderId: source.orderId, receiptId: source.receiptId, billId: source.billId, billLineKey: source.billLineKey,
        materialId: source.materialId, quoteOptionId: source.quoteOptionId, materialLabel: source.materialLabel, unit: source.unit,
        qty, rate: source.rate, amount: roundMoney(qty * source.rate),
        destination: destinations[0],
        destinations,
        allocationMode: destinations.length > 1 ? 'shared' as const : 'single' as const,
        stockIssueId: line.stockIssueId, note: line.note.trim(), allocatedDate,
      };
    });
}
